import { Button } from '@/components/ui/button'
import { UsersIcon } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

export interface OverviewActionBarProps {
  /** Where the respondent is — "Overview" here; a section title deeper in. */
  title: string
  /** Autosave status line. Omitted when there is nothing to report. */
  savedLabel?: string | null
  canEdit: boolean
  /** The viewer may submit (Application Owner); independent of `canEdit`. */
  canSubmit: boolean
  submitEnabled: boolean
  submitting?: boolean
  onSubmit?: () => void
  onManageCollaborators?: () => void
}

/**
 * Sticky bar above the assessment content: where you are, whether your work is saved,
 * and the two assessment-level actions.
 */
export function OverviewActionBar({
  title,
  savedLabel,
  canEdit,
  canSubmit,
  submitEnabled,
  submitting = false,
  onSubmit,
  onManageCollaborators,
}: OverviewActionBarProps) {
  return (
    <div className="sticky top-16 z-40 border-b border-border bg-surface">
      <div className="flex flex-wrap items-center gap-3 px-6 py-3.5 lg:px-10">
        <span className="font-jakarta text-[17px] font-medium text-heading">{title}</span>

        <div className="ml-auto flex flex-wrap items-center gap-3">
          {savedLabel && (
            <span className="flex items-center gap-2 text-[15px] text-body">
              <span className="h-2 w-2 rounded-full bg-success" aria-hidden="true" />
              {savedLabel}
            </span>
          )}

          {canEdit && onManageCollaborators && (
            <Button
              variant="outline"
              onClick={onManageCollaborators}
              className="gap-2 rounded-card border-primary bg-surface px-4 text-[15px] font-semibold text-primary hover:bg-surface-hover hover:text-primary"
            >
              <UsersIcon />
              Manage collaborators
            </Button>
          )}

          {canSubmit && (
            <Button
              disabled={!submitEnabled || submitting}
              aria-busy={submitting}
              onClick={onSubmit}
              className={cn(
                'rounded-card px-6 text-[15px] font-semibold shadow-none',
                !submitEnabled &&
                  'cursor-not-allowed bg-success-bg text-body disabled:pointer-events-auto disabled:opacity-100',
              )}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
